import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header6 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template6' },
        { label: 'Shop', path: '/template6/shop' },
        { label: 'About', path: '/template6/about' },
        { label: 'Contact', path: '/template6/contact' },
        { label: 'Orders', path: '/template6/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Display:wght@400;600;700&family=Nunito:wght@300;400;600&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? '#FAF7F2' : '#F5F0E8',
                    borderBottom: scrolled ? '1px solid #D4C5B0' : '1px solid transparent',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
                    boxShadow: scrolled ? '0 2px 20px rgba(139, 69, 19, 0.08)' : 'none',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '72px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template6')}
                        style={{
                            fontFamily: "'Noto Serif Display', Georgia, serif",
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#8B4513',
                            cursor: 'pointer',
                            letterSpacing: '2px',
                            margin: 0,
                        }}
                    >
                        DHARTI
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '32px',
                        }}
                        className="dharti-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Nunito', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#2C1810',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    transition: 'color 0.3s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#8B4513')}
                                onMouseLeave={(e) => (e.target.style.color = '#2C1810')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <Search
                            size={20}
                            style={{ color: '#2C1810', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#8B4513')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#2C1810')}
                        />
                        <User
                            size={20}
                            style={{ color: '#2C1810', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template6/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#8B4513')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#2C1810')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template6/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#2C1810', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#8B4513')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#2C1810')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#8B4513',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        fontFamily: "'Nunito', sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="dharti-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={24} style={{ color: '#2C1810' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#F5F0E8',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={28} style={{ color: '#2C1810' }} />
                </button>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '28px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Noto Serif Display', Georgia, serif",
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#8B4513',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                letterSpacing: '3px',
                                textTransform: 'uppercase',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#6B3410')}
                            onMouseLeave={(e) => (e.target.style.color = '#8B4513')}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .dharti-desktop-nav {
                    display: flex !important;
                }
                .dharti-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .dharti-desktop-nav {
                        display: none !important;
                    }
                    .dharti-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header6;
